import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth-context';
import AccountTab from './settings/AccountTab';
import PropertiesTab from './settings/PropertiesTab';
import UsersTab from './settings/UsersTab';
import SystemTab from './settings/SystemTab';

/**
 * Tabbed Settings page. Account / Properties always visible; Users
 * only for admins; System always visible (health + sign-out + ops).
 *
 * The active tab can be set via ?tab=… so the property switcher's
 * "Add property" link can deep-link straight here.
 */
export default function Settings() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'account';

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="space-y-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          System
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          Settings
        </h1>
        <p className="text-muted-foreground">
          Account, properties, members, and the plumbing behind HouseOps.
        </p>
      </header>

      <Tabs
        value={tab}
        onValueChange={(next) => {
          const np = new URLSearchParams(params);
          np.set('tab', next);
          setParams(np, { replace: true });
        }}
      >
        <TabsList>
          <TabsTrigger value="account" data-testid="tab-account">
            Account
          </TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">
            Properties
          </TabsTrigger>
          {isAdmin ? (
            <TabsTrigger value="users" data-testid="tab-users">
              Users
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="system" data-testid="tab-system">
            System
          </TabsTrigger>
        </TabsList>
        <TabsContent value="account" className="pt-4">
          <AccountTab />
        </TabsContent>
        <TabsContent value="properties" className="pt-4">
          <PropertiesTab />
        </TabsContent>
        {isAdmin ? (
          <TabsContent value="users" className="pt-4">
            <UsersTab />
          </TabsContent>
        ) : null}
        <TabsContent value="system" className="pt-4">
          <SystemTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
